
//@ts-check

import React, { useState, useEffect } from "react";

import "../styles/WebLandingPage.css";
import "../styles/MobileWebLandingPage.css";
import "../styles/WebLoadingText.css";
import "../styles/MobileDonorCard.css";


import ResultsLoadingManagerWebInstance from "./ResultsLoadingManagerWebInstance";
import { resultAtomsToParsedDonors } from "./paginator";
import { getResultsFor } from "./GetQueryResultsFunctions";
import MobileChooseCampaignLightbox from "./MobileChooseCampaignLightbox";
import CampaignList, { getQueryName } from "./CampaignList";
import DonorCardWebLayout from "./DonorCardWebLayout";
import NamedBuildingList from "./NamedBuildingList";
import { pushDonorListIsPopulated } from "./AnalyticsFunctions";


// @ts-ignore
import searchIcon from "../images/search_icon--black.svg";
// @ts-ignore
import searchIconBlue from "../images/search_icon--blue.svg";
// @ts-ignore
import searchIconWhite from "../images/search_icon--white.svg";
// @ts-ignore
import donateBtnImg from "../images/donate_icon.svg";
// @ts-ignore
import browseImg from "../images/browse.svg";
// @ts-ignore
import removeSearchIcon from "../images/remove_search--white.svg";



/**
 * @typedef {import("./coreTypes").QuerySpec} QuerySpec
 * @typedef {import('./coreTypes').CampaignObject} CampaignObject
 * @typedef {import('./coreTypes').ResultAtom} ResultAtom
 * @typedef {import('./coreTypes').JSResultAtom} JSResultAtom
 */
  

/**
 * @param {{ 
 *      campaign: CampaignObject | null, 
 *      onCampaignChanged: (a: CampaignObject) => void, 
 *      performDonorSearch: (a: string) => void }} props 
 * @returns {JSX.Element | null}
 */

function MobileWebResultsCampaignPage(props) {

    const [showCampaignLightBox, setShowCampaignLightBox] = useState(false);
    /**
     * @type {[JSResultAtom[], React.Dispatch<React.SetStateAction<JSResultAtom[]>>]}
     */
    // @ts-ignore
    const [donors, setDonors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDonorSearch, setShowDonorSearch] = useState(false);
    const [donorSearchString, setDonorSearchString] = useState("");
    const [isNamedBuildings, setIsNamedBuildings] = useState(false);
    const [lastFilterStringUsed, setLastFilterStringUsed] = useState('');
    const [filterString, setFilterString] = useState('');
    const [isError, setIsError] = useState(false);


    const shouldDisplayRemoveFilterIcon = 
        (lastFilterStringUsed !== '') && (lastFilterStringUsed === filterString);

    const selectCampaign = (campQueryName) => {
        doIfGetCampaignObjectFromQueryName(campQueryName, props.onCampaignChanged);
    };

    const toggleShowDonorSearch = () => { setShowDonorSearch(!showDonorSearch); };


    const comb = ResultsLoadingManagerWebInstance.generateCombinedMethodsFromSetter(setDonors);
    const generateLoadingIdAndSetAsActive = comb.generateLoadingIdAndSetAsActive;
    const setDonorsIfCurrentLoadingId = comb.setResultsIfCurrentLoadingId; 



     /**
     * @param {QuerySpec} querySpec 
     * @param {function(ResultAtom[]):JSResultAtom[]} resToDonors 
     * @param {boolean} useAnalytics
     * @param {number}[size=0] size
     * @return {void}
     */
      const setDonorsIncrementally = (querySpec, resToDonors, useAnalytics, size) => {

        if (size === 0 || size === undefined) size = 50;

        const id = generateLoadingIdAndSetAsActive();
        setIsLoading(true);
        setDonors([]);

        /** @type {boolean} */
        let analyticsSent = false;

        const errorHandler = _ => { setIsLoading(false); setIsError(true); };
        const resultsObj = getResultsFor(querySpec, 50);
        /**
         * @type {function(ResultAtom[]):void}
         */
        let nextHandler;
        nextHandler = (res) => {
            const donors = resToDonors(res);

            if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() === id
                && donors.length > 0) {
                if (useAnalytics && !analyticsSent) {
                    analyticsSent = true;
                    pushDonorListIsPopulated(true);
                }
                setDonors(d => d.concat(donors));
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
            else if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() === id
                && res.length === 0) {
                if (useAnalytics) {
                    pushDonorListIsPopulated(false);
                }
                setIsLoading(false);
            }
            else if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() !== id) {
                // NOTE: Don't set isLoading to false here, because this was preempted
                // by either another thing loading here, or another loading on a different
                // page (and thus the visuals here being irrelevant).
            }
            else {
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
        };
        resultsObj.initial.then(res => {
            const donors = resToDonors(res);
            setDonorsIfCurrentLoadingId(id, donors);
            setIsError(false);

            if (donors.length > 0) {
                if (useAnalytics && !analyticsSent) {
                    analyticsSent = true;
                    pushDonorListIsPopulated(true);
                }
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
            else if (res.length === 0) {
                if (useAnalytics) {
                    pushDonorListIsPopulated(false);
                }
                setIsLoading(false);
            }
            else {
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
        }).catch(errorHandler);
    };


    useEffect(() => {

        if (props.campaign === null || 
                (props.campaign !== null 
                    && getQueryName(props.campaign) === 'Named Buildings')) {
            setDonors([]);
            setIsLoading(false);
            setIsError(false);
            setIsNamedBuildings(true);
            return;
        }
        else {
            setIsNamedBuildings(false);
        }

        /** @type {QuerySpec} */
        const querySpec = [['campaign_name', getQueryName(props.campaign)]];

        setDonorsIncrementally(querySpec, resultAtomsToParsedDonors, false);
        
    // eslint-disable-next-line
    }, [props.campaign]);


     /**
     * @param {string} filter 
     * @returns {void}
     */
      const performFiltering = (filter) => {

        if (props.campaign === null) return;

        const campQueryName = getQueryName(props.campaign);
        if (campQueryName !== 'Named Buildings') {

            setLastFilterStringUsed(filter);

            /** @type {QuerySpec} */
            const querySpec = [['campaign_name', campQueryName]]

            /**
              * @param {ResultAtom[]} res 
              * @returns {JSResultAtom[]}
              */
            const resultAtomsToFilteredDonors = res => 
                resultAtomsToParsedDonors(res).filter(d => { 
                    const fullName = d[1].firstName + ' ' + d[1].lastName; 
                    return fullName
                        .toLocaleLowerCase()
                        .includes(filter.toLocaleLowerCase());
                });

            setDonorsIncrementally(
                querySpec, 
                r =>
                    (filter === '') 
                        ? resultAtomsToParsedDonors(r) 
                         : resultAtomsToFilteredDonors(r),
                true,
                300);
        }
    };

    const clearOutFilter = () => {
        setFilterString("");
        setLastFilterStringUsed("");
        performFiltering("");
    };

    function rtrim(str, chr) {
        if (str.indexOf(',') > -1) {
            str = str.split(',').reverse().join(' ');
        }
        var rgxtrim = (!chr) ? new RegExp('\\s+$') : new RegExp(chr+'+$');
        return str.replace(rgxtrim, '');
      }

    const performSearch = () => {
        props.performDonorSearch(rtrim(donorSearchString));
    };


    return (
        <>
        {showCampaignLightBox && 
            <MobileChooseCampaignLightbox 
                cancelLightbox={() => { setShowCampaignLightBox(false); }} 
                selectCampaign={selectCampaign} />}
         <div className="web-app mobile-web">
            { (props.campaign !== null && getQueryName(props.campaign) !== 'Named Buildings')
            ? <div style={containerStyle}>
                    {/**@ts-ignore */}
                    <div style={campaignTitleContainerStyle}>
                        <p style={campaignTitleStyle}>{props.campaign.title}</p>
                    </div>
                    {/**@ts-ignore */}
                    <img style={campaignImageStyle} 
                        src={props.campaign.image.full.url}
                        alt={props.campaign.title}/>
                    <div style={campaignInfoContainerStyle}>
                        {/**@ts-ignore */}
                        <p style={campaignInfoTextStyle}>
                            {props.campaign.info}
                        </p>
                    </div>
               </div>
            : null }
            { isNamedBuildings && <NamedBuildingsDisplay/> }
            { !isNamedBuildings &&
            <>
                <p style={{ marginTop: 20, }}/>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: '100%', maxWidth: 443, marginLeft: 10, marginRight: 10, }}>
                        {/**@ts-ignore */}
                        <p style={donorsHeaderTextStyle}>Donors to this campaign</p>
                        {/**@ts-ignore */}
                        <div style={filterDonorNamesContainerStyle}>
                                <input 
                                    type="text" 
                                    id="web-res-campaign-filter-donor-names" 
                                    placeholder="Filter donor names" 
                                    aria-label="Filter donor names - names are filtered as you type"
                                    /** @ts-ignore */
                                    style={filterDonorNamesInputStyle}                                     
                                    value={filterString}
                                    onChange={e => setFilterString(e.target.value)} 
                                    onKeyUp={e => {
                                    if (e.key === "Enter") { performFiltering(filterString); }  
                                    }}/>
                                {/** @ts-ignore */}
                                <img style={filterDonorSearchIconStyle} 
                                    src={searchIconWhite} 
                                    onClick={() => performFiltering(filterString)}
                                    alt="Filter Donors" />
                                { shouldDisplayRemoveFilterIcon && 
                                    <img 
                                        src={removeSearchIcon} 
                                        /** @ts-ignore */
                                        style={removeFilterIconStyle}
                                        onClick={clearOutFilter}
                                        alt="Remove Filter Text" />}
                            </div>
                    </div>
                </div>
            </> }
            <p style={{ marginTop: 20, }}/>
            { !isNamedBuildings && !isError && !isLoading 
                && donors.length === 0 && <p style={errorStyle}>There are no results.</p>}
            { !isNamedBuildings && !isError && <DonorCardWebLayout donors={donors} singleColumn={true} /> }
            { !isNamedBuildings && !isLoading && isError && <p style={errorStyle}>There was an error fetching the list of donors.</p> }
            { (!isNamedBuildings && isLoading)
                ? <div className="web-loading-text__container">
                    <p className="web-loading-text__loading">Loading</p>
                    <p className="web-loading-text__dot-1"> . </p>
                    <p className="web-loading-text__dot-2"> . </p>
                    <p className="web-loading-text__dot-3"> . </p>
                  </div> 
                : null }
            <p style={{ marginTop: 120, }}/>
        </div>
        {/** @ts-ignore */}
        <span style={(showDonorSearch) ? bottomNavWithSearchBorderStyle : bottomNavBorderStyle} />
        {/** @ts-ignore */}
        <div style={(showDonorSearch) ? bottomNavWithSearchContainerStyle : bottomNavContainerStyle}>
            {/** @ts-ignore */}
            <table style={(showDonorSearch) ? bottomNavWithSearchTableStyle : bottomNavTableStyle}>
                <tbody>
                    <tr>
                        <td style={bottomNavTdStyle}>
                            {/** @ts-ignore */}
                            <div style={bottomNavCellStyle} 
                                onClick={toggleShowDonorSearch}>
                                <img src={(showDonorSearch) ? searchIconBlue : searchIcon} alt="" />
                                <p style={(showDonorSearch) ? bottomNavWithSearchSearchTextStyle : bottomNavTextStyle}>Search</p>
                            </div>
                        </td>
                        <td style={bottomNavTdStyle}>
                            {/** @ts-ignore */}
                            <a style={bottomNavCellStyle}
                                href="https://www.liberty.edu/giving/" >
                                <img src={donateBtnImg} alt="" />
                                <p style={bottomNavTextStyle}>Donate</p>
                            </a>
                        </td>
                        <td style={bottomNavTdStyle}>
                            {/** @ts-ignore */}
                            <div style={bottomNavCellStyle} 
                                onClick={() => { setShowCampaignLightBox(true); } }>
                                <img src={browseImg}  alt="" />
                                <p style={bottomNavTextStyle}>Browse</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            { showDonorSearch && 
            /** @ts-ignore */
            <div style={searchContainerStyle}>
                <input 
                    type="text"
                    style={searchFieldStyle} 
                    value={donorSearchString} 
                    placeholder="Search for a donor by name"
                    aria-label="Search for a donor by name"
                    onChange={e => setDonorSearchString(e.target.value)} 
                    onKeyUp={e => {
                        if (e.key === "Enter") { performSearch(); }
                    }} />
                <input type="button" style={searchButtonStyle} onClick={performSearch} />
                {/** @ts-ignore */}
                <img style={searchIconStyle} 
                    src={searchIconWhite}
                    alt="Search"  />
                </div>}
        </div>
        </>
    );
}


const containerStyle = {

};

const errorStyle = {
    fontFamily: 'Arial',
    color: 'white',
    fontSize: 14
};


// Campaign card styles


const campaignTitleContainerStyle = {
    backgroundColor: 'white',
    textAlign: 'center',
    verticalAlign: 'center',
    width: '100%',
    minHeight: 120,
    display: 'flex',
};

const campaignTitleStyle = {
    margin: 'auto',
    fontSize: 40,
    fontFamily: "cinzel",
};

const campaignImageStyle = {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    maxHeight: 400,
};

const campaignInfoContainerStyle = {
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.156)',
};

const campaignInfoTextStyle = {
    color: 'white',
    fontFamily: 'Arial',
    fontSize: '12pt',
    padding: 15,
    textAlign: 'left',
    lineHeight: 1.5,
};




// Filter styles

const donorsHeaderTextStyle = {
    color: 'white',
    fontFamily: 'Arial',
    fontSize: '16pt',
    fontWeight: 'bold',
    textAlign: 'left',
    width: '100%',
};

const filterDonorNamesContainerStyle = {
    position: 'relative',
    width: '100%',
    display: 'flex',
};

const filterDonorNamesInputStyle = {
    width: '100%',
    maxWidth: 240,
    height: 40,
    fontFamily: 'Arial',
    fontSize: '11pt',
    color: 'white',
    background: 'transparent',
    paddingLeft: 35,
    borderBottomWidth: 2,
    borderBottomColor: 'white',
    borderBottomStyle: 'solid',
};

const filterDonorSearchIconStyle = {
    position: 'absolute',
    left: 0,
    bottom: 5,
    cursor: 'pointer',
};


const removeFilterIconStyle = {
    position: 'relative',
    left: -25,
    padding: 4,
    alignSelf: 'center',
    cursor: 'pointer',
};




// Bottom Navigation styles

const bottomNavBorderStyle = {
    background: 'linear-gradient(to top, gray, transparent)',
    height: 5,
    width: '100%',
    position: 'fixed',
    bottom: 50,
};

const bottomNavWithSearchBorderStyle = {
    ...bottomNavBorderStyle,
    bottom: 100,
};

const bottomNavContainerStyle = {
    backgroundColor: 'white',
    height: 50,
    position: 'fixed',
    bottom: 0,
    width: '100%',
};

const bottomNavWithSearchContainerStyle = {
    ...bottomNavContainerStyle,
    height: 100,
};


const bottomNavTableStyle = { 
    height: '100%', 
    width: '100%', 
    tableLayout: 'fixed', 
};


const bottomNavWithSearchTableStyle = {
    ...bottomNavTableStyle,
    height: '50%',
};


const bottomNavTdStyle = {
    height: '100%',
    width: '100%',
    verticalAlign: 'middle',
};

const linkStyleUndoStyle = {
    color: 'black',
    textDecoration: 'none',
}

const bottomNavCellStyle = {
    ...linkStyleUndoStyle,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    cursor: 'pointer',
};

const bottomNavTextStyle = {
    fontFamily: 'Arial',
    fontSize: '14pt',
    marginLeft: 10,
};

const bottomNavWithSearchSearchTextStyle = {
    ...bottomNavTextStyle,
    color: '#2773cb',
}

const searchContainerStyle = {
    position: 'relative',
    width: '90%',
    display: 'flex',
    flexDirection: 'row',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 10,
};

const searchFieldStyle =  {
    border: '2px solid gray',
    width: '100%',
    height: 45,
    fontFamily: 'Arial',
    paddingLeft: 10,
};

const searchButtonStyle = {
    width: 45,
    height: 45,
    backgroundColor: '#2773cb',
    cursor: 'pointer',
};

const searchIconStyle = {
    position: 'absolute',
    right: -10,
    width: 45,
    height: 45,
    padding: 8,
    margin: 10,
    alignSelf: 'center',
    pointerEvents: 'none',
};



function doIfGetCampaignObjectFromQueryName(queryName, action) {
    const res = CampaignList.find(c => getQueryName(c) === queryName );
    if (res !== undefined) action(res);
}


function NamedBuildingsDisplay() {

    const buildingTitleContainerStyle = {
        backgroundColor: 'white',
        textAlign: 'center',
        verticalAlign: 'center',
        width: '100%',
        minHeight: 120,
        display: 'flex',
    };
    
    const buildingTitleStyle = {
        margin: 'auto',
        fontSize: '20pt',
        fontFamily: "cinzel",
    };
    
    const buildingImageStyle = {
        objectFit: 'cover',
        width: '100%',
        height: '100%',
        maxHeight: 400,
    };
    
    const buildingInfoContainerStyle = {
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.156)',
    };
    
    const buildingInfoTextStyle = {
        color: 'white',
        fontFamily: 'Arial',
        fontSize: '12pt',
        padding: 15,
        textAlign: 'left',
        lineHeight: 1.5,
    };

    const buildings = 
        NamedBuildingList.map(n => (
            <>
            <div>
                {/** @ts-ignore */}
                <div style={buildingTitleContainerStyle}>
                    <p style={buildingTitleStyle}>{n.title}</p>
                </div>
                {/** @ts-ignore */}
                <img src={n.image} style={buildingImageStyle} alt={n.title}/>
                <div style={buildingInfoContainerStyle}>
                    {/** @ts-ignore */}
                    <p style={buildingInfoTextStyle}>{n.info}</p>
                </div>
            </div>
            <p style={{ marginTop: 30, }} />
            </>
        ));

    return (<>{buildings}</>);
}

export default MobileWebResultsCampaignPage;